import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutFetch } from "../../../store/authSlice";
import { getLoginPath } from "../../../utils/authRedirect";
import { heroBackgroundStyle } from "../../../utils/clientHeroTheme";
import { IconBell, IconSettings } from "../../ui/icons";
import GlobalPatientSearch from "../GlobalPatientSearch";
import DesktopSidebar, { desktopSidebarStyles as ds } from "./DesktopSidebar";
import md from "./manageDesktop.module.css";
import {
  adminFirstName,
  adminGreeting,
  adminTodayLabel,
} from "../AdminPortal/adminAppUtils";
import L from "../../../constants/baysportLabels";
import { normalizeRole } from "../../../utils/roles";

const ROLE_LABEL = {
  owner: "Владелец",
  admin: "Администратор",
  trainer: L.trainer,
};

export default function ManageDesktopShell({
  children,
  homeTo,
  brandRole,
  sections,
  chatBadge = 0,
  notifyBadge = 0,
  notifyOpen,
  onNotifyToggle,
  notifyBtnRef,
  settingsTo,
  profileTo,
  pageTitle,
  isOverview,
  isSchedule,
  isJournal,
  isLife = false,
  isProfile = false,
  isSettings,
  isChat = false,
  flushContent,
  getBadge,
  shellStyle,
  showSearch = true,
  brandingTheme = null,
  personalizationTo = null,
  showHeroSettings = true,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tenant, profile, role } = useSelector((state) => state.auth);
  const r = normalizeRole(role);
  const firstName = adminFirstName(profile?.full_name);

  const logout = () => {
    dispatch(logoutFetch());
    navigate(getLoginPath(role));
  };

  const badgeFn =
    getBadge ||
    ((item) => (item.id === "chat" ? chatBadge : 0));

  const profileInitials = String(profile?.full_name || "?")
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const profileChip = profileTo ? (
    <Link
      to={profileTo}
      className={ds.userChip}
      title="Профиль"
      aria-label="Профиль"
    >
      <div className={ds.userAvatar} aria-hidden>
        {profileInitials}
      </div>
      <span className={ds.userName}>{profile?.full_name || "—"}</span>
    </Link>
  ) : (
    <div className={ds.userChip}>
      <div className={ds.userAvatar} aria-hidden>
        {profileInitials}
      </div>
      <span className={ds.userName}>{profile?.full_name || "—"}</span>
    </div>
  );

  const showOverviewHero = isOverview && brandingTheme;
  // If we render the overview hero, don't also render a separate page header.
  const showPageHead =
    !showOverviewHero &&
    !isSchedule &&
    !isChat &&
    (isOverview || (Boolean(pageTitle) && !isJournal && !isSettings && !isLife && !isProfile));
  const fullHeightContent = isSchedule || isJournal || isSettings || isLife || isProfile;
  const flushFullViewport = isSchedule || isChat || isJournal || isLife || isProfile;
  const heroStyle = brandingTheme ? heroBackgroundStyle(brandingTheme) : null;

  return (
    <div className={ds.desktopRoot} style={shellStyle}>
      <DesktopSidebar
        homeTo={homeTo}
        brandName={tenant?.name || L.club}
        brandRole={brandRole || ROLE_LABEL[r]}
        sections={sections}
        onLogout={logout}
        getBadge={badgeFn}
      />

      <main className={ds.main}>
        <header className={ds.topHeader}>
          <div className={ds.topHeaderInner}>
            {showSearch ? (
              <div className={`${ds.topHeaderSearch} ${md.topBarSearch}`}>
                <GlobalPatientSearch />
              </div>
            ) : (
              <div className={ds.topHeaderSpacer} aria-hidden />
            )}
            <div className={ds.topHeaderActions}>
              <button
                ref={notifyBtnRef}
                type="button"
                className={ds.iconBtn}
                title="Уведомления"
                aria-label="Уведомления"
                aria-expanded={notifyOpen}
                onClick={onNotifyToggle}
              >
                <IconBell width={18} height={18} />
                {notifyBadge > 0 ? (
                  <span className={ds.navBadge}>
                    {notifyBadge > 9 ? "9+" : notifyBadge}
                  </span>
                ) : null}
              </button>
              {settingsTo ? (
                <Link
                  to={settingsTo}
                  className={ds.iconBtn}
                  title={personalizationTo ? "Персонализация" : "Настройки"}
                >
                  <IconSettings width={18} height={18} />
                </Link>
              ) : null}
              {profileChip}
            </div>
          </div>
        </header>

        <div className={ds.mainBody}>
        <div
          className={
            flushFullViewport
              ? ds.contentScheduleFlush
              : fullHeightContent
                ? ds.contentSchedule
                : ds.mainScroll
          }
          data-pull-scroll={!fullHeightContent && !flushFullViewport ? true : undefined}
        >
          {showOverviewHero ? (
            <div className={md.desktopOverviewHeroWrap}>
              <div
                className={`${md.desktopOverviewHero} ${
                  brandingTheme.coverUrl ? md.desktopOverviewHeroCover : ""
                }`}
                style={{
                  ...heroStyle,
                  fontFamily: brandingTheme.fontFamily,
                }}
              >
                <div className={md.desktopOverviewHeroContent}>
                  <p className={md.desktopOverviewHeroDate}>{adminTodayLabel()}</p>
                  <h1 className={md.desktopOverviewHeroTitle}>
                    {adminGreeting()}
                    {firstName ? `, ${firstName}` : ""}
                  </h1>
                  {tenant?.name ? (
                    <p className={md.desktopOverviewHeroClub}>{tenant.name}</p>
                  ) : null}
                </div>
                {personalizationTo && showHeroSettings ? (
                  <Link
                    to={personalizationTo}
                    className={md.desktopOverviewHeroSettings}
                    title="Персонализация"
                    aria-label="Персонализация"
                  >
                    <IconSettings width={18} height={18} />
                  </Link>
                ) : null}
              </div>
            </div>
          ) : null}

          {showPageHead ? (
            <div className={ds.pageHead}>
              <div className={ds.pageHeadRow}>
                <div>
                  {isOverview ? (
                    <>
                      <h1 className={ds.topBarTitle}>
                        {adminGreeting()}
                        {firstName ? `, ${firstName}` : ""}
                      </h1>
                      <p className={ds.topBarMeta}>{adminTodayLabel()}</p>
                    </>
                  ) : (
                    <h1 className={ds.topBarTitle}>{pageTitle}</h1>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          <div
            className={
              flushFullViewport
                ? ds.contentScheduleInner
                : flushContent || fullHeightContent
                  ? ds.contentFlush
                  : ds.content
            }
          >
            <div
              className={
                isSchedule
                  ? md.innerSchedule
                  : isChat
                    ? md.innerChat
                    : isJournal
                      ? md.innerJournal
                      : isLife
                        ? md.innerClub
                        : isProfile
                          ? md.innerProfile
                          : flushContent || fullHeightContent
                        ? md.innerWide
                        : md.inner
              }
            >
              {children}
            </div>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
}
